import React from 'react'
import Lottie from 'react-lottie'
import CallToAction from 'components/MainNav/Submenus/CallToAction'
import * as ProductIcons from 'components/ProductIcons'
import * as NotProductIcons from 'components/NotProductIcons'
import { Link } from 'gatsby'
import { GatsbyImage, getImage, StaticImage } from 'gatsby-plugin-image'
import { prepareData } from 'utils/googleData'

interface PopularPageProps {
    icon: JSX.Element
    name: string
    url: string
}

const differentReady = ({ data, placeholder }: { data: any; placeholder: React.ReactElement }) => {
    return data ? (
        <Lottie
            options={{
                loop: true,
                autoplay: true,
                animationData: data,
            }}
        />
    ) : (
        placeholder
    )
}

export const PopularPage = ({ Icon, name, url }: PopularPageProps) => {
    return (
        <li className="list-none border-r border-dashed border-gray-accent-dark pr-1 last:border-r-0 last:pr-0">
            <div
                to={url}
                className="group relative flex flex-col items-center rounded p-4 text-white hover:bg-white/10
                    active:top-[0.5px]
                    active:scale-[.98]"
            >
                <div
                    className="mb-2 flex h-12 w-12 items-center justify-center rounded-full group-hover:text-white

                "
                >
                    <div className="box-border h-8 w-8">{Icon && <Icon />} </div>
                </div>
                <h4 className="text-center text-base font-semibold leading-tight">{name}</h4>
            </div>
        </li>
    )
}

const Star = () => {
    return (
        <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" className="h-5 w-5">
            <path
                d="M19.669 8.027 14.845 12l2.377 6.79c.126.36.014.76-.281 1.003a.906.906 0 0 1-1.038.08L10 16.403l-5.903 3.472A.91.91 0 0 1 2.78 18.79L5.155 12 .33 8.027a.909.909 0 0 1 .578-1.61h6.2L9.142.609a.91.91 0 0 1 1.716 0l2.033 5.808h6.2a.91.91 0 0 1 .578 1.61Z"
                fill="#F1A82C"
            />
        </svg>
    )
}

const AboutVisionCTASubSection = ({ cta }) => {
    return (
        <>
            <h3 className="mb-2 text-4xl">{cta?.title}</h3>
            <h4 className="font-semibold leading-tight text-white/70">
                {cta?.subtitle}
                {cta?.url && <Link to={cta?.url}>{cta?.name}</Link>}
            </h4>
        </>
    )
}
const AboutVisionPillarsSubSection = ({ pillars }) => {
    return (
        <>
            <p className="opacity-70">{pillars?.title}</p>
            {pillars?.pages?.length > 0 && (
                <ul className="m-0 mb-2 grid grid-cols-3 gap-x-1 p-0">
                    {pillars.pages?.map(({ name, icon, url }, index) => {
                        const Icon = NotProductIcons[icon] || ProductIcons[icon]

                        return <PopularPage key={name} Icon={Icon} name={name} url={url} />
                    })}
                </ul>
            )}
            <CallToAction to={pillars?.ctaUrl} type="secondary" className="!w-full">
                {pillars?.ctaName}
            </CallToAction>
        </>
    )
}

const AboutVisionTitleSubSectionSide = ({ image, title, hideImage }) => {
    return (
        <>
            <div className="max-w-sm border-2 border-solid border-white/50 px-8 pt-6 pb-10">
                <div className="mb-3 flex items-center justify-between border-b border-dashed border-gray-accent-light pb-3">
                    <h4 className="mb-0 text-xl">{title?.title}</h4>
                    <div className="flex space-x-1">
                        <Star />
                        <Star />
                        <Star />
                        <Star />
                        <Star />
                    </div>
                </div>
                {title?.subtitle}
            </div>

            {!hideImage && image && (
                <GatsbyImage
                    image={image}
                    imageStyle={{ objectFit: `contain` }}
                    alt="A robot team work"
                    className="-mx-4 -mt-6 md:-mt-16 mdlg:-mx-0 mdlg:mt-8 lg:-mt-10"
                />
            )}
        </>
    )
}

export const AboutVision = (props) => {
    const {
        enable,
        story,
        subtitle,
        description,
        cta,
        cta2,
        svgImage,
        gatsbyImage,
        gatsbyImage2,
        aboutVisionTitle,
        id,
        style,
        pillars,
        hideImage,
    } = prepareData({
        props,
    })
    if ((enable === false || enable?.toLowerCase?.() === 'false') && !process.env.RENDER_DISABLED_COMPONENTS) {
        return <></>
    }

    const [differentReadyData, setDifferentReadyData] = React.useState<any | null>(null)
    React.useEffect(() => {
        import('../../../../static/animations/about-ready-1.json').then((data) => setDifferentReadyData(data.default))
    }, [])
    return (
        <section id={id} className={`relative mb-12 px-4 pt-12 mdlg:px-8 mdlg:pt-16 ${style}`}>
            <div className="mx-auto flex max-w-5xl flex-col-reverse gap-8 text-white mdlg:flex-row mdlg:items-end">
                <aside className="relative flex h-full basis-[60%] flex-col">
                    <AboutVisionTitleSubSectionSide
                        image={gatsbyImage}
                        title={aboutVisionTitle}
                        hideImage={hideImage}
                    />
                </aside>
                <div className="flex-1 mdlg:pb-16">
                    <AboutVisionCTASubSection cta={cta} />
                    <AboutVisionPillarsSubSection pillars={pillars} />
                </div>
            </div>
        </section>
    )
}
